import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet"; // Added for meta tags
import { FaUserCircle, FaComments, FaListAlt, FaShareAlt, FaChartLine, FaUsers } from "react-icons/fa";
import TrvllrSocial from "./TrvllrSocial";
import { Lightbulb, ClipboardList, Medal, Star } from "lucide-react";
import WaitlistForm from "./WaitlistForm";
import { Check as CheckIcon, X as XIcon } from "lucide-react";
import JourneyImage from '../assets/tech.png';
import Logo from '../assets/logo.png';
import AboutUs from "./AboutUs";
import PlatformVisualizer from "./PlatformVisualizer";
import Contact from "./Contact";
import { 
  ChevronRight, ChevronDown, Menu, X,
  FileText, Users, Calendar, Bot,
  Briefcase, MapPin, Shield, BarChart2,
  Clock
} from 'lucide-react';

// Smooth scroll helper function
const smoothScroll = (event, targetId) => {
  event.preventDefault();
  const targetElement = document.getElementById(targetId);
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// Simple form component for hero section
const SimpleWaitlistForm = ({ sharedEmail, setSharedEmail }) => {
    const [email, setEmail] = useState(sharedEmail || "");
  
    const handleSubmit = (e) => {
      e.preventDefault();
      
      if (typeof setSharedEmail === "function") { // ✅ Ensure it's a function before calling
        setSharedEmail(email);
        console.log("✅ Quick form submitted with email:", email);
      } else {
        console.error("❌ setSharedEmail is not a function");
      }
  
      document.getElementById("form").scrollIntoView({ behavior: "smooth" });
    };
  
    return (
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          className="flex-grow px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:border-purple-500 focus:outline-none"
        />
        <button 
          type="submit" 
          className="px-6 py-2 bg-gradient-to-r from-purple-500 to-amber-500 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
        >
          Join Waitlist
        </button>
      </form>
    );
  };
    
// Testimonial data
const testimonials = [
  {
    id: 1,
    name: "Sarah J.",
    role: "Travel Nurse, ICU",
    text: "Trvllr has transformed how I manage my travel assignments. The document organization alone has saved me countless hours!",
    rating: 5
  },
  {
    id: 2,
    name: "Michael T.",
    role: "Radiologic Technologist",
    text: "The T-Bot assistant helped me find a contract with 15% higher pay than my previous one. Game changer for my career.",
    rating: 5
  },
  {
    id: 3,
    name: "Elena M.",
    role: "Respiratory Therapist",
    text: "I was skeptical at first, but the location insights feature helped me find amazing opportunities in cities I'd never considered.",
    rating: 4
  }
];

const ProLanding = () => {
  const [showFeatures, setShowFeatures] = useState(false);
  const [spotsRemaining, setSpotsRemaining] = useState(250);
  const [daysTillLaunch, setDaysTillLaunch] = useState(45);

  // Countdown effect to create urgency
  useEffect(() => {
    const timer = setTimeout(() => {
      if (spotsRemaining > 200) {
        setSpotsRemaining(spotsRemaining - 1);
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, [spotsRemaining]);

  const mainFeatures = [
    { name: 'Document Management', icon: FileText, desc: 'Centralized credential management & automatic expiration alerts' },
    { name: 'Recruiter Management', icon: Users, desc: 'Streamlined agency communications & offer comparison' },
    { name: 'Shift Manager', icon: Calendar, desc: 'Intelligent schedule tracking with automatic time logging' },
    { name: 'T-Bot Assistant', icon: Bot, desc: 'AI-powered career guidance & document handling' }
  ];

  const solutions = [
    { name: 'AutoJM™', icon: Briefcase, desc: 'AI-powered job board, driving seamless matching and acquisition with alerts and rewards' },
    { name: 'TrIPS™', icon: MapPin, desc: 'Location insights and planning' },
    { name: 'TrInsyte™', icon: BarChart2, desc: 'Market analytics platform' },
    { name: 'T-Med™', icon: Shield, desc: 'Private medical insurance marketplace' }
  ];

  const metrics = [
    { value: '1.2M', label: 'TAM (Total Addressable Market)', desc: 'Travel healthcare professionals in the US' },
    { value: '450k', label: 'SAM (Serviceable Available Market)', desc: 'Actively working travel healthcare professionals' },
    { value: '120k', label: 'SOM (Serviceable Obtainable Market)', desc: 'Expected early adopters of Trvllr™ in 3 years' },
    { value: '38%', label: 'Staffing Shortages', desc: 'Facilities reporting critical shortages' },
    { value: '70%', label: 'Dependence on Travel Professionals', desc: 'Facilities using travel professionals to meet demands' },
    { value: '10%', label: 'Projected Annual Growth', desc: 'Expected yearly growth rate for the travel healthcare industry' },
  ]; 

  const footerLinks = {
    Platform: [
      { name: "Document Management", id: "features" },
      { name: "Recruiter Management", id: "features" },
      { name: "Shift Manager", id: "features" },
      { name: "Marketplace", id: "features" }
    ],
    Solutions: [
      { name: "AutoJM™", id: "solutions" },
      { name: "TrIPS™", id: "solutions" },
      { name: "TrInsyte™", id: "solutions" },
      { name: "ISO-Auction™", id: "iso-auction" }
    ],
    Support: [
      { name: "Privacy Policy", id: "privacy-policy" },
      { name: "Terms of Service", id: "terms-of-service" },
      { name: "Community", id: "social" },
      { name: "Employers", id: "employers" }
    ],
    Company: [
      { name: "About Us", id: "about-us" },
      { name: "Contact", id: "contact" }
    ]
  };  

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Added SEO Meta Tags */}
      <Helmet>
        <title>Trvllr - Intelligent Career Companion for Healthcare Professionals</title>
        <meta name="description" content="Transform your healthcare journey with Trvllr's AI-powered job matching, document management, and career tools. Join thousands of professionals who work smarter." />
        <meta name="keywords" content="travel healthcare, healthcare jobs, allied health, radtech, radiologic technologist, travel nurse, healthcare professionals, career management" />
        {/* Open Graph Tags for social sharing */}
        <meta property="og:title" content="Trvllr - Intelligent Career Companion for Healthcare Professionals" />
        <meta property="og:description" content="Transform your healthcare journey with Trvllr's AI-powered career ecosystem." />
        <meta property="og:type" content="website" />
        {/* Structured Data for better SEO */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "Trvllr",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "description": "Career management platform for travel healthcare professionals",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            }
          `}
        </script>
      </Helmet>

      <nav id="top" className="fixed w-full bg-gray-900 border-b border-gray-800 z-50">
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <a href="#hero" className="h-16 w-16 rounded-lg flex items-center justify-center logo-hover">
                <img
                  src={Logo}
                  alt="Trvllr Logo"
                  className="h-12 w-auto sm:h-16 lg:h-20 object-contain bg-transparent"
                  loading="eager" // Optimize for above-the-fold image
                />
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <div className="relative group">
                <button className="text-gray-400 hover:text-white flex items-center">
                  Features
                </button>
                <div className="absolute top-full left-0 mt-2 w-64 bg-gray-800 rounded-lg shadow-xl border border-gray-700 hidden group-hover:block">
                  {mainFeatures.map((feature, index) => (
                    <a
                      key={index}
                      href="#features"
                      className="flex items-center space-x-3 px-4 py-3 hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg"
                    >
                      <feature.icon className="text-purple-400" size={18} />
                      <span className="text-gray-300">{feature.name}</span>
                    </a>
                  ))}
                </div>
              </div>
              <a href="#solutions" className="text-gray-400 hover:text-white">Solutions</a>
              <a href="#employers" className="text-gray-400 hover:text-white">For Employers</a>
              <a href="#testimonials" className="text-gray-400 hover:text-white">Testimonials</a>
              <a href="#beta" className="text-gray-400 hover:text-white">Pricing (Beta)</a>
              <a href="#iso-auction" className="text-amber-400 hover:text-white">ISO-Auction</a>
              <a href="#about-us" className="text-gray-400 hover:text-white">About Us</a>
              <a
                href="#contact"
                className="text-gray-400 hover:text-white px-6 py-2 border border-gray-600 rounded-lg transition duration-300"
              >
                Contact
              </a>
            </div>
            <a href="#form" className="bg-gradient-to-r from-purple-500 to-amber-500 px-8 py-4 rounded-lg text-white font-medium text-lg hover:opacity-90 transition-opacity flex items-center">
              <span>Join Beta</span>
              <span className="ml-2 text-sm bg-white text-purple-700 px-2 py-1 rounded-full">
                {spotsRemaining} spots left
              </span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section - Optimized with immediate form */}
        <section id="hero" className="relative pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Left side - Content */}
              <div className="lg:w-1/2 text-center lg:text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                  Welcome to <span className="bg-gradient-to-r from-purple-500 to-amber-500 bg-clip-text text-transparent">Trvllr™,</span> Your Intelligent Career Companion
                </h1>
                <p className="text-2xl text-purple-400 font-medium mb-4">
                  <span className="animated-word delay-0">Work. </span>
                  <span className="animated-word delay-1">Wander. </span>
                  <span className="animated-word delay-2">Win. </span>
                </p>
                <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto lg:mx-0">
                  Transform your healthcare journey with AI-powered job matching, smart document management, and career insights.
                </p>
                
                {/* Urgency indicators */}
                <div className="flex items-center justify-center lg:justify-start gap-4 mb-6">
                  <div className="flex items-center">
                    <Clock className="text-purple-400 mr-2" size={18} />
                    <span className="text-white">Beta launches in <strong className="text-amber-400">{daysTillLaunch} days</strong></span>
                  </div>
                  <div className="flex items-center">
                    <Users className="text-purple-400 mr-2" size={18} />
                    <span className="text-white"><strong className="text-amber-400">{spotsRemaining}</strong> spots remaining</span>
                  </div>
                </div>
                
                {/* Simple waitlist form in hero section */}
                <div className="mb-8">
                  <SimpleWaitlistForm />
                </div>
              </div>
              
              {/* Right side - Image */}
              <div className="lg:w-1/2">
                <img
                  src={JourneyImage}
                  alt="Trvllr Platform Interface"
                  className="rounded-lg shadow-lg max-w-full mx-auto"
                  width="600" 
                  height="400"
                  loading="eager" // Optimize for above-the-fold image
                />
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof - Added NEW section for testimonials */}
        <section id="testimonials" className="py-16 bg-gray-800/30">
          <div className="max-w-7xl mx-auto px-14">
            <h2 className="text-3xl font-bold text-white text-center mb-3">
              What Healthcare Professionals Say
            </h2>
            <p className="text-lg text-gray-300 text-center mb-10 max-w-2xl mx-auto">
              Join hundreds of healthcare professionals already experiencing the Trvllr™ difference
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={16} 
                        className={i < testimonial.rating ? "text-amber-400 fill-amber-400" : "text-gray-600"} 
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4">"{testimonial.text}"</p>
                  <div className="mt-4">
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-purple-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Benefits Section - NEW section highlighting top 3 benefits */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-12">
              How Trvllr™ Transforms Your Career
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300 flex flex-col items-center">
                <Briefcase className="text-purple-400 w-12 h-12 mb-4" />
                <h3 className="text-white font-bold text-xl mb-2">Smart Job Matching</h3>
                <p className="text-gray-400">AI-powered matching finds you opportunities with 15-20% higher pay than industry average</p>
              </div>
              
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300 flex flex-col items-center">
                <FileText className="text-purple-400 w-12 h-12 mb-4" />
                <h3 className="text-white font-bold text-xl mb-2">Document Automation</h3>
                <p className="text-gray-400">Save time with automatic document organization, updates, and expiration alerts</p>
              </div>
              
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300 flex flex-col items-center">
                <BarChart2 className="text-purple-400 w-12 h-12 mb-4" />
                <h3 className="text-white font-bold text-xl mb-2">Market Insights</h3>
                <p className="text-gray-400">Make data-driven career decisions with real-time analytics on pay rates and location trends</p>
              </div>
            </div>
            
            <a href="#features" className="text-purple-400 hover:text-white mt-4 inline-flex items-center">
              See all features <ChevronRight className="ml-1" size={16} />
            </a>
          </div>
        </section>

        {/* ✅ Platform Visualizer Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Explore the <span className="bg-gradient-to-r from-purple-500 to-amber-500 bg-clip-text text-transparent">Trvllr™ Ecosystem</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              Our AI-driven platform unifies powerful tools for healthcare professionals and recruiters in one seamless experience.
            </p>

            {/* Embedding PlatformVisualizer Component */}
            <PlatformVisualizer />
          </div>
        </section>

        {/* Metrics Section - Kept and slightly optimized */}
        <section className="py-20 bg-gray-800/50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Industry at a Glance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {metrics.map((metric, index) => (
                <div key={index} className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                  <div className="text-4xl font-bold text-purple-400 mb-4">{metric.value}</div>
                  <h3 className="text-lg text-white font-semibold mb-2">{metric.label}</h3>
                  <p className="text-gray-400">{metric.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Features Section - Condensed */}
        <section id="features" className="py-20 bg-gray-800/50">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Core Platform Features
            </h2>
            <p className="text-xl text-gray-300 text-center mb-8 max-w-2xl mx-auto">
              Everything you need to manage your travel healthcare career in one intelligent platform
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mainFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300"
                >
                  <feature.icon className="text-purple-400 w-8 h-8 mb-4" />
                  <h3 className="text-white font-bold text-lg mb-2">{feature.name}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Mid-page CTA - NEW */}
            <div className="mt-12 p-6 bg-gray-800/80 rounded-lg border border-purple-500/50 mx-auto max-w-3xl">
              <h3 className="text-2xl font-bold text-white mb-3">Ready to transform your career?</h3>
              <p className="text-gray-300 mb-4">Join the waitlist today and be among the first to experience Trvllr™.</p>
              <a 
                href="#form" 
                className="bg-gradient-to-r from-purple-500 to-amber-500 px-6 py-3 rounded-lg text-white font-medium inline-flex items-center hover:opacity-90 transition-opacity"
              >
                Reserve Your Spot
                <ChevronRight className="ml-1" size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* Solutions Grid - Kept but with improved descriptions */}
        <section id="solutions" className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-white text-center mb-6">
              Comprehensive Solutions
            </h2>
            <p className="text-xl text-gray-300 text-center mb-8 max-w-2xl mx-auto">
              Powerful tools designed specifically for travel healthcare professionals
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                  <solution.icon className="text-purple-400 w-8 h-8 mb-4" />
                  <h3 className="text-white font-bold text-lg mb-2">{solution.name}</h3>
                  <p className="text-gray-400">{solution.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* T-Bot Section - Condensed for focus */}
        <section id="t-bot" className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Meet <span className="bg-gradient-to-r from-purple-500 to-amber-500 bg-clip-text text-transparent">T-Bot™</span>
            </h2>
            <p className="text-lg text-gray-300 mb-12 max-w-2xl mx-auto">
              Your intelligent partner in travel healthcare. Simplify your workflow with integrations across the platform, save time, and stay ahead with T-Bot™'s AI-powered assistance.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition-all">
                <h3 className="text-xl font-semibold text-white mb-4">Document Management</h3>
                <p className="text-gray-400">
                  Organize, verify, and share documents seamlessly. Use OCR to digitize your paperwork and automate transfers.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition-all">
                <h3 className="text-xl font-semibold text-white mb-4">Live Resume Updates</h3>
                <p className="text-gray-400">
                  Update your resume dynamically using voice or text prompts. T-Bot™ pulls from your contract history to keep credentials current.
                </p>
              </div>

              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition-all">
                <h3 className="text-xl font-semibold text-white mb-4">Career Insights</h3>
                <p className="text-gray-400">
                  Receive personalized career analytics and guidance through TrInsyte™. Break down contracts, negotiate better, and discover new opportunities.
                </p>
              </div>
            </div>

            <div className="mt-8">
              <button
                onClick={() => document.getElementById("form").scrollIntoView({ behavior: "smooth" })}
                className="bg-gradient-to-r from-purple-500 to-amber-500 px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
              >
                Discover T-Bot™
              </button>
            </div>
          </div>
        </section>

        {/* ISO Auction Section - Kept with slight optimization */}
        <section id="iso-auction" className="iso-auction relative text-white">
          <div className="absolute inset-0">
            <img src={require("../assets/pros1.png")} 
                alt="Healthcare Professionals" 
                className="w-full h-full object-cover opacity-60"
                loading="lazy" />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          <div className="relative z-10 container mx-auto px-6 py-16 text-center">
            <h2 className="section-title text-4xl font-bold">ISO-Auction: Competitive Wage Bidding, On Your Terms</h2>
            <p className="section-description mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
              Empower yourself to take control of your contracts. 
              Instigate an <strong>ISO (In Search Of) Request</strong> and let recruiters compete to match your terms.
            </p>

            <div className="auction-features grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              <div className="feature-card bg-white/10 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold">Set Your Terms</h3>
                <p className="mt-2 text-gray-300">Define your preferred pay, benefits, location, and work conditions.</p>
              </div>
              <div className="feature-card bg-white/10 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold">Live Bidding</h3>
                <p className="mt-2 text-gray-300">Watch recruiters submit competing offers to meet your terms.</p>
              </div>
              <div className="feature-card bg-white/10 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold">Smart Matching</h3>
                <p className="mt-2 text-gray-300">TrInsyte™ helps optimize offers based on market trends.</p>
              </div>
              <div className="feature-card bg-white/10 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold">Seamless Negotiation</h3>
                <p className="mt-2 text-gray-300">AutoNeg™ ensures the best possible contract terms.</p>
              </div>
            </div>

            <div className="cta-section mt-8">
              <a href="#form" className="cta-button bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg inline-block">
                Try ISO-Auction
              </a>
            </div>
          </div>
        </section>

        {/* Comparison Section - Kept but moved higher for better conversion */}
        <section id="comparison" className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Why Choose Trvllr™?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              See how Trvllr™ stacks up against popular platforms.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-700 text-left text-sm text-gray-300">
                <thead>
                  <tr className="bg-gray-800 text-gray-400">
                    <th className="border border-gray-700 px-4 py-2">Features</th>
                    <th className="border border-gray-700 px-4 py-2">Trvllr™</th>
                    <th className="border border-gray-700 px-4 py-2">Vivian</th>
                    <th className="border border-gray-700 px-4 py-2">Bluepipes</th>
                    <th className="border border-gray-700 px-4 py-2">Indeed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-800 hover:bg-purple-500/20 transition-colors duration-300">
                    <td className="border border-gray-700 px-4 py-3">Live Resume Updates</td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-800 hover:bg-purple-500/20 transition-colors duration-300">
                    <td className="border border-gray-700 px-4 py-3">AI-Driven Job Matching</td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-800 hover:bg-purple-500/20 transition-colors duration-300">
                    <td className="border border-gray-700 px-4 py-3">Intelligent Document Management</td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-800 hover:bg-purple-500/20 transition-colors duration-300">
                    <td className="border border-gray-700 px-4 py-3">Smart Scheduling with Auto-Timelog</td>
                    <td><CheckIcon className="text-green-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                    <td><XIcon className="text-red-500 w-6 h-6 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Social Features */}
        <TrvllrSocial />

        {/* Employers Section - Kept but condensed */}
        <section id="employers" className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-white text-center mb-6">
              For Recruiters & Employers
            </h2>
            <p className="text-xl text-gray-300 text-center mb-8 max-w-2xl mx-auto">
              Simplify staffing with Trvllr™. Discover top talent, streamline hiring processes, 
              and ensure your facility operates at full capacity.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">Access Top Talent</h3>
                <p className="text-gray-400">
                  Connect with highly skilled travel healthcare professionals 
                  across the nation.
                </p>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">Streamlined Hiring</h3>
                <p className="text-gray-400">
                  Use our AI-powered tools to match professionals to your specific needs.
                </p>
              </div>
              <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 hover:border-purple-500 transition-all duration-300">
                <h3 className="text-xl font-bold text-white mb-4">Real-Time Insights</h3>
                <p className="text-gray-400">
                  Gain access to market data and analytics to make informed staffing decisions.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <a
                href="#form"
                className="bg-gradient-to-r from-purple-500 to-amber-500 px-8 py-4 rounded-lg text-white font-medium text-lg hover:opacity-90 transition-opacity flex items-center justify-center mx-auto"
                style={{ maxWidth: "300px" }}
              >
                Get Started Today
                <ChevronRight className="inline-block ml-2" />
              </a>
            </div>
          </div>
        </section>

        {/* Beta Section - Enhanced with more urgency */}
        <section id="beta" className="py-20 bg-gray-900 relative">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="bg-gray-800/50 p-8 rounded-lg border border-purple-500/30">
              <h2 className="text-3xl font-bold text-white mb-2">
                Join the exclusive <span className="text-purple-400 font-bold">Trvllr Beta</span> program <span className="text-amber-400">today</span>
              </h2>
              <p className="text-lg text-gray-300 mb-6 max-w-3xl mx-auto">
                Beta access is <span className="text-purple-400 font-bold">FREE</span> for a limited testing period (4 months). 
                <span className="text-amber-400 font-semibold"> Only {spotsRemaining} spots remaining!</span>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                  <Lightbulb size={40} className="text-purple-400 mx-auto mb-4" />
                  <h3 className="text-white font-bold mb-2">Purpose of Beta</h3>
                  <p className="text-gray-400">
                    Test and refine Trvllr™'s tools with feedback from real professionals.
                  </p>
                </div>

                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                  <ClipboardList size={40} className="text-purple-400 mx-auto mb-4" />
                  <h3 className="text-white font-bold mb-2">What You'll Get</h3>
                  <p className="text-gray-400">
                    Full access to all platform features, priority support, and influence on development.
                  </p>
                </div>

                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                  <Medal size={40} className="text-purple-400 mx-auto mb-4" />
                  <h3 className="text-white font-bold mb-2">Post-Beta Perks</h3>
                  <p className="text-gray-400">
                    Lifetime discounts, early feature access, and "Founding Member" recognition.
                  </p>
                </div>
              </div>

              <div className="flex justify-center">
                <a
                  href="#form"
                  className="bg-gradient-to-r from-purple-500 to-amber-500 px-8 py-4 rounded-lg text-white font-medium text-lg hover:opacity-90 transition-opacity flex items-center"
                >
                  <span>Secure Your Beta Access</span>
                  <Clock className="ml-2" size={20} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* About Us - Kept */}
        <AboutUs />

        {/* CTA Section - Enhanced with urgent messaging */}
        <section id="form" className="py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-3">
              Ready to Transform Your Travel Healthcare Career?
            </h2>
            <p className="text-xl text-gray-300 mb-4 max-w-2xl mx-auto">
              Join {1000 - spotsRemaining} healthcare professionals already on the Trvllr™ waitlist.
            </p>
            <div className="flex items-center justify-center mb-6">
              <Clock className="text-purple-400 mr-2" size={20} />
              <span className="text-amber-400 font-semibold">Limited-time beta access - only {spotsRemaining} spots remaining!</span>
            </div>
            <WaitlistForm />
            <p className="text-sm text-gray-400 mt-4">
              By joining the waitlist, you'll be among the first to access Trvllr's powerful suite of career tools.
              We'll never share your information with third parties.
            </p>
          </div>
        </section>
      </main>

      {/* Contact - Kept */}
      <Contact />

      {/* Footer - Kept with enhancement */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {Object.entries(footerLinks).map(([section, links]) => (
              <div key={section}>
                <h3 className="text-white font-bold mb-4">{section}</h3>
                <ul className="space-y-2">
                  {links.map((link, index) => (
                    <li key={index}>
                      {link.name === "Privacy Policy" ? (
                        <Link to="/privacy-policy" className="text-gray-400 hover:text-white">
                          Privacy Policy
                        </Link>
                      ) : link.name === "Terms of Service" ? (
                        <Link to="/tos" className="text-gray-400 hover:text-white">
                          Terms of Service
                        </Link>
                      ) : (
                        <a href={`#${link.id}`} className="text-gray-400 hover:text-white">
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {/* Added structured SEO footer */}
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} Trvllr™. All rights reserved.</p>
            <p className="mt-2">Empowering healthcare professionals to work smarter, not harder.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProLanding;